import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import {
  Avatar,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  makeStyles
} from "@material-ui/core";
import { CallEnd, Mic, MicOff, VolumeUp } from "@material-ui/icons";
import { AuthContext } from "../Auth/AuthContext";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";

const VoiceRoomContext = createContext();

const useStyles = makeStyles(theme => ({
  bar: {
    position: "fixed",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 1400,
    width: 62,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    padding: "9px 7px",
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 12px 36px rgba(0, 0, 0, 0.18)",
    [theme.breakpoints.down("xs")]: {
      top: "auto",
      right: "auto",
      left: 10,
      bottom: 76,
      transform: "none",
      width: "auto",
      flexDirection: "row",
      borderRadius: 30,
      padding: "7px 9px"
    }
  },
  avatar: {
    width: 38,
    height: 38,
    background: theme.palette.success.main
  },
  details: {
    textAlign: "center",
    maxWidth: 48
  },
  roomName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: 10
  },
  remoteAudio: {
    display: "none"
  }
}));

export const VoiceRoomProvider = ({ children }) => {
  const classes = useStyles();
  const { isAuth } = useContext(AuthContext);
  const [rooms, setRooms] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [muted, setMuted] = useState(false);
  const [remoteStreams, setRemoteStreams] = useState({});
  const socketRef = useRef();
  const localStreamRef = useRef();
  const currentRoomRef = useRef();
  const peersRef = useRef(new Map());
  const pendingCandidatesRef = useRef(new Map());
  const iceServersRef = useRef([
    { urls: "stun:stun.l.google.com:19302" }
  ]);

  const loadRooms = useCallback(async () => {
    if (!isAuth) return;
    try {
      setRooms((await api.get("/voice-rooms")).data);
    } catch (err) {
      toastError(err);
    }
  }, [isAuth]);

  const closePeer = useCallback(socketId => {
    peersRef.current.get(socketId)?.close();
    peersRef.current.delete(socketId);
    pendingCandidatesRef.current.delete(socketId);
    setRemoteStreams(current => {
      const next = { ...current };
      delete next[socketId];
      return next;
    });
  }, []);

  const closeAllPeers = useCallback(() => {
    peersRef.current.forEach(peer => peer.close());
    peersRef.current.clear();
    pendingCandidatesRef.current.clear();
    setRemoteStreams({});
    setParticipants([]);
  }, []);

  const createPeer = useCallback(
    socketId => {
      const existing = peersRef.current.get(socketId);
      if (existing) return existing;

      const peer = new RTCPeerConnection({
        iceServers: iceServersRef.current
      });
      localStreamRef.current?.getTracks().forEach(track => {
        peer.addTrack(track, localStreamRef.current);
      });
      peer.onicecandidate = event => {
        if (event.candidate) {
          socketRef.current?.emit("voice:signal", {
            target: socketId,
            signal: { candidate: event.candidate }
          });
        }
      };
      peer.ontrack = event => {
        setRemoteStreams(current => ({
          ...current,
          [socketId]: event.streams[0]
        }));
      };
      peer.onconnectionstatechange = () => {
        if (["failed", "closed"].includes(peer.connectionState)) {
          closePeer(socketId);
        }
      };
      peersRef.current.set(socketId, peer);
      return peer;
    },
    [closePeer]
  );

  const flushCandidates = useCallback(async socketId => {
    const peer = peersRef.current.get(socketId);
    const candidates = pendingCandidatesRef.current.get(socketId) || [];
    if (!peer?.remoteDescription) return;
    await Promise.all(candidates.map(candidate => peer.addIceCandidate(candidate)));
    pendingCandidatesRef.current.delete(socketId);
  }, []);

  const leaveRoom = useCallback(() => {
    socketRef.current?.emit("voice:leave");
    closeAllPeers();
    localStreamRef.current?.getTracks().forEach(track => track.stop());
    localStreamRef.current = null;
    currentRoomRef.current = null;
    setCurrentRoom(null);
    setMuted(false);
  }, [closeAllPeers]);

  const registerInRoom = useCallback(room => {
    socketRef.current?.emit("voice:join", { roomId: room.id }, response => {
      if (!response?.ok) {
        leaveRoom();
        return;
      }
      currentRoomRef.current = room;
      setCurrentRoom(room);
      setParticipants([response.participant, ...response.participants]);
    });
  }, [leaveRoom]);

  const joinRoom = useCallback(
    async room => {
      if (currentRoomRef.current?.id === room.id) return;
      leaveRoom();

      try {
        iceServersRef.current = (
          await api.get("/voice-rooms/ice-servers")
        ).data;
        localStreamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          },
          video: false
        });
        registerInRoom(room);
      } catch (err) {
        leaveRoom();
        toastError(err);
      }
    },
    [leaveRoom, registerInRoom]
  );

  const toggleMute = useCallback(() => {
    setMuted(currentMuted => {
      const nextMuted = !currentMuted;
      localStreamRef.current?.getAudioTracks().forEach(track => {
        track.enabled = !nextMuted;
      });
      socketRef.current?.emit("voice:state", { muted: nextMuted });
      return nextMuted;
    });
  }, []);

  useEffect(() => {
    if (!isAuth) {
      leaveRoom();
      return undefined;
    }

    loadRooms();
    const socket = openSocket();
    socketRef.current = socket;

    socket.on("connect", () => {
      if (currentRoomRef.current && localStreamRef.current) {
        closeAllPeers();
        registerInRoom(currentRoomRef.current);
      }
    });
    socket.on("disconnect", closeAllPeers);
    socket.on("voice:rooms:update", loadRooms);
    socket.on("voice:presence", ({ roomId, count }) => {
      setRooms(current =>
        current.map(room =>
          Number(room.id) === Number(roomId)
            ? { ...room, participantCount: count }
            : room
        )
      );
    });
    socket.on("voice:user-joined", async participant => {
      setParticipants(current => [
        ...current.filter(item => item.socketId !== participant.socketId),
        participant
      ]);
      const peer = createPeer(participant.socketId);
      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      socket.emit("voice:signal", {
        target: participant.socketId,
        signal: { description: peer.localDescription }
      });
    });
    socket.on("voice:user-left", ({ socketId }) => {
      closePeer(socketId);
      setParticipants(current =>
        current.filter(item => item.socketId !== socketId)
      );
    });
    socket.on("voice:user-state", participant => {
      setParticipants(current =>
        current.map(item =>
          item.socketId === participant.socketId ? participant : item
        )
      );
    });
    socket.on("voice:signal", async ({ from, signal }) => {
      const peer = createPeer(from);
      if (signal.description) {
        await peer.setRemoteDescription(signal.description);
        await flushCandidates(from);
        if (signal.description.type === "offer") {
          const answer = await peer.createAnswer();
          await peer.setLocalDescription(answer);
          socket.emit("voice:signal", {
            target: from,
            signal: { description: peer.localDescription }
          });
        }
      }
      if (signal.candidate) {
        if (peer.remoteDescription) {
          await peer.addIceCandidate(signal.candidate);
        } else {
          const candidates = pendingCandidatesRef.current.get(from) || [];
          candidates.push(signal.candidate);
          pendingCandidatesRef.current.set(from, candidates);
        }
      }
    });
    socket.on("voice:room-closed", leaveRoom);

    return () => {
      leaveRoom();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [
    closeAllPeers,
    closePeer,
    createPeer,
    flushCandidates,
    isAuth,
    leaveRoom,
    loadRooms,
    registerInRoom
  ]);

  return (
    <VoiceRoomContext.Provider
      value={{
        rooms,
        currentRoom,
        participants,
        muted,
        loadRooms,
        joinRoom,
        leaveRoom,
        toggleMute
      }}
    >
      {children}
      {Object.entries(remoteStreams).map(([socketId, stream]) => (
        <audio
          className={classes.remoteAudio}
          key={socketId}
          autoPlay
          ref={element => {
            if (element && element.srcObject !== stream) {
              element.srcObject = stream;
            }
          }}
        />
      ))}
      {currentRoom && (
        <Paper className={classes.bar} elevation={8}>
          <Tooltip title={`Conectado em ${currentRoom.name}`}>
            <Avatar className={classes.avatar}>
              <VolumeUp fontSize="small" />
            </Avatar>
          </Tooltip>
          <div className={classes.details}>
            <Typography className={classes.roomName} variant="subtitle2">
              {participants.length} online
            </Typography>
          </div>
          <Tooltip title={muted ? "Ativar microfone" : "Silenciar"}>
            <IconButton size="small" onClick={toggleMute}>
              {muted ? <MicOff color="error" /> : <Mic />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Sair da sala">
            <IconButton size="small" onClick={leaveRoom}>
              <CallEnd color="error" />
            </IconButton>
          </Tooltip>
        </Paper>
      )}
    </VoiceRoomContext.Provider>
  );
};

export const useVoiceRoom = () => useContext(VoiceRoomContext);
